/**
 * useStreamingMissionChat Hook
 *
 * Converts streaming-service WebSocket events into chat messages.
 * Replaces useMissionChat for the streaming path.
 *
 * Handles:
 * - mission.agent.turn → chat message bubbles
 * - mission.hitl.request → interactive prompt
 * - mission.hitl.response → resolved prompt
 * - mission.user.message.receipt → the delivery of a message sent while the
 *   agent works (useUserMessageDelivery)
 * - mission.question.answer.result / mission.question.closed → the host's
 *   answer to an answer attempt, and a question it ended (useAnswerDelivery)
 * - mission.tool.call / tool.result → collapsible tool cards
 * - mission.node.status → status indicators
 * - mission.ended → completion/failure banner
 * - mission.transcript.interim/final → voice transcript bubbles
 * - mission.operator.turn → a person's reply, mission.operator.hold → its
 *   notice, mission.operator.end_user_turn → the end user's message during
 *   the hold, mission.operator.message.receipt → that operator's reply
 *   (operator conversation takeover, agion/Agion#3435)
 */

import { choiceQuestion, freeTextExpiry, questionCountdown, questionOptionDetails, type ChoiceQuestion, type QuestionOptionDetail } from '../utils/questionOptions'
import { useState, useCallback, useEffect, useRef, useMemo, type Dispatch, type SetStateAction } from 'react'
import {
  MessageTypeDelegation,
  MessageTypeGovernanceDecision,
  MessageTypeOperatorEndUserTurnEvent,
  MessageTypeOperatorHoldEvent,
  MessageTypeOperatorTurnEvent,
  MessageTypeToolCall,
  MessageTypeToolResult,
  isQuestionClosedReason,
  knownForcedReason,
  streamMessageKind,
  toolResultViewFiles,
  type ChatMessageKind,
  type QuestionClosedReason,
} from '@agion/mission-stream'
import type { QuestionReply } from '@agion/mission-stream'
import { getMissionStatusService } from '../services/streaming/mission-status'
import { operatorTakeoverEnabled } from '../utils/config'
import { rowFiles, useAnswerDelivery, type ReturnedAnswer } from './useAnswerDelivery'
import { useUserMessageDelivery } from './useUserMessageDelivery'
import { useExecutionStore } from './useExecutionStore'
import { useMissionStore } from './useMissions'
import { toolCallContent, toolResultContent, withLiveToolMessage } from '../utils/live-tool-messages'
import { questionCardOf } from '../utils/questionCard'
import type {
  MissionSubscriptionOptions,
  NodeStatusUpdateMessage,
  AgentTurnMessage,
  ToolCallMessage,
  ToolResultMessage,
  HITLRequestMessage,
  HITLResponseMessage,
  OrchestratorDecisionMessage,
  DelegationMessage,
  DelegationResultMessage,
  AgentThinkingMessage,
  GovernanceDecisionMessage,
  MissionTriggerMessage,
  MissionCompletedMessage,
  MissionFailedMessage,
  UserMessageReceiptMessage,
  UserMessageQueuedMessage,
  QuestionAnswerResultMessage,
  QuestionClosedMessage,
  ConnectionRestoredMessage,
  OperatorHoldMessage,
  OperatorTurnMessage,
  OperatorEndUserTurnMessage,
  OperatorMessageReceiptMessage,
  TranscriptInterimMessage,
  TranscriptFinalMessage,
  VoiceRoomReadyMessage,
  NodeExecutionStatus,
  HITLDecision,
  HITLAttachment,
} from '../types/streaming'

/**
 * The relay's refusals of a stop, pause or resume: the person may not
 * control the run (control_forbidden), or whether they may could not be
 * checked (control_unavailable, agion/Agion#3024). Either way the run goes
 * on as it was.
 */
const CONTROL_REFUSALS: ReadonlySet<string> = new Set(['control_forbidden', 'control_unavailable'])

/**
 * A streaming chat message for rendering. Its kinds are the placement module's
 * (@agion/mission-stream), which decides where each one shows.
 */
export interface StreamingChatMessage {
  id: string
  type: ChatMessageKind
  timestamp: number
  missionId: string
  nodeId?: string
  agentId?: string
  data: Record<string, unknown>
}

export type { QuestionOptionDetail } from '../utils/questionOptions'

/**
 * One reply of the person who holds the run's conversation
 * (agion/Agion#3435), from sending to the host's receipt: accepted, or
 * refused with the reason it gave. not_sent: nothing left the browser.
 */
export interface OperatorReply {
  messageId: string
  holdId: string
  text: string
  status: 'sending' | 'accepted' | 'refused' | 'not_sent'
  reason?: string
}

/** HITL request state */
export interface ActiveHITLRequest {
  requestId: string
  executionId?: string
  nodeId: string
  agentId: string
  prompt: string
  options?: string[]
  /**
   * The server's label and description for some of the options, keyed by
   * option: what a choice shows under each option (persona walkthrough
   * F-CHAT-06). The answer is still the option itself.
   */
  optionDetails?: QuestionOptionDetail[]
  /** The structured choice question, when the frame carries one (agion/Agion#3559). */
  question?: ChoiceQuestion
  /** A free text question's expires_at: it stays open until then (agion/Agion#3559). */
  expiresAt?: string
  modality: string
  timeoutMs?: number
  receivedAt: number
  /** Kind categorizes the intervention type: user_prompt (default), policy_review, operator_handoff, chat_handoff */
  kind?: 'user_prompt' | 'policy_review' | 'operator_handoff' | 'chat_handoff'
  /** Governance context that triggered this HITL pause, if any */
  governanceContext?: {
    ruleName: string
    severity: string
    action?: string
    /** The persisted escalation this card decides (agion/Agion#2618). */
    escalationId?: string
    /** Approve and Deny are offered on the card only when its runner decides them inline (agion/Agion#2618). */
    inlineDecision: boolean
  }
}

/**
 * The rows with a live question in place of the history row that echoes it.
 * The Edge host keeps a question in its chat history as the question's text
 * only, with no request id and no choices (mission-runner pkg/chat
 * reportHITLRequest), and the relay replays the open question with both. The
 * echo the live question replaces is the latest row with its text that no
 * answer follows; an earlier question with the same text that was answered
 * stays. With no such echo the question is appended.
 */
function withLiveQuestion(rows: StreamingChatMessage[], question: StreamingChatMessage): StreamingChatMessage[] {
  const prompt = typeof question.data.prompt === 'string' ? question.data.prompt.trim() : ''
  for (let i = rows.length - 1; prompt && i >= 0; i--) {
    const row = rows[i]!
    if (row.type === 'hitl_response') break
    if (row.type === 'hitl_request' && !row.data.requestId
      && typeof row.data.prompt === 'string' && row.data.prompt.trim() === prompt) {
      return [...rows.slice(0, i), question, ...rows.slice(i + 1)]
    }
  }
  return [...rows, question]
}

/** Whether the row is the question card of the request (agion/Agion#3446). */
function isCardRow(row: StreamingChatMessage, requestId: string): boolean {
  return row.type === 'hitl_request' && !!row.data.questionCard && row.data.requestId === requestId
}

/** Voice room connection info */
export interface VoiceRoomInfo {
  serverUrl: string
  participantToken: string
  roomName: string
  participantIdentity: string
  executionId: string
}

/** Node status tracking */
export interface NodeStatus {
  nodeId: string
  status: NodeExecutionStatus
  agentId?: string
  startedAt?: string
  completedAt?: string
  error?: string
}

/** Execution statistics */
export interface ExecutionStats {
  totalInputTokens: number
  totalOutputTokens: number
  turnCount: number
  toolCallCount: number
  durationMs: number
}

interface UseStreamingMissionChatOptions {
  missionId: string | null
  executionId?: string | null
  enabled?: boolean
  /** Skip WebSocket subscription (e.g., for completed executions loaded from history) */
  skipSubscription?: boolean
  /**
   * The run takes a message while the agent works, at its next turn: an
   * adaptive or autonomous run (phase 6). Without it a running mission takes
   * a message only as the answer to the selected free-text question.
   */
  acceptsMessages?: boolean
  /**
   * The server restored the run after its host reconnected (P-095 Amendment
   * S04-7): the caller reads the run's messages from the server again.
   */
  onConnectionRestored?: () => void
}

interface UseStreamingMissionChatResult {
  messages: StreamingChatMessage[]
  nodeStatuses: Map<string, NodeStatus>
  /** @deprecated Use selectedHITL instead. Alias kept for backward compat. */
  activeHITL: ActiveHITLRequest | null
  /** All pending HITL requests (excluding message-only modality) */
  pendingHITLs: ActiveHITLRequest[]
  /** Index of the currently selected HITL in pendingHITLs */
  selectedHITLIndex: number
  /** The currently selected pending HITL (convenience alias for pendingHITLs[selectedHITLIndex]) */
  selectedHITL: ActiveHITLRequest | null
  /** Navigate to a specific pending HITL by index */
  selectHITL: (index: number) => void
  isConnected: boolean
  isCompleted: boolean
  isFailed: boolean
  error: string | null
  /** Code of the error frame behind `error`, when the server sent one. */
  errorCode: string | null
  stats: ExecutionStats
  voiceRoom: VoiceRoomInfo | null
  /**
   * Sends the text as the answer to the selected free-text question, or, in a
   * run that accepts messages with no question open, as a message the agent
   * reads at its next turn; false when nothing was sent.
   */
  sendMessage: (message: string, attachments?: HITLAttachment[]) => boolean
  /** decision: only for an approval card decided inline (agion/Agion#2618). */
  respondToHITL: (requestId: string, response: string, attachments?: HITLAttachment[], decision?: HITLDecision, answer?: QuestionReply) => void
  /** The last answer that failed or was refused, with its text and files, for the composer to restore (phase 4). */
  returnedAnswer: ReturnedAnswer | null
  notifyVoiceUserJoined: () => void
  /** F5: request mission cancellation. Translates to mission.cancel on the
   *  Edge host. No-op when there is no executionId or the WS is offline. */
  stopMission: () => void
  /** True after pauseMission() until resumeMission() or a terminal event.
   *  Local state — the host pauses at its next checkpoint. */
  isPaused: boolean
  /** Request execution pause on the Edge host (next checkpoint).
   *  No-op when there is no executionId or the WS is offline. */
  pauseMission: () => void
  /** Resume a previously paused execution. */
  resumeMission: () => void
  /** Seed historical messages (only works when messages array is empty) */
  setInitialMessages: (msgs: StreamingChatMessage[]) => void
  /**
   * Sends a reply of the person who holds the conversation as
   * mission.operator.message for the hold (agion/Agion#3435); returns its
   * message id, or null when operator takeover is off or there is no run.
   */
  sendOperatorMessage: (holdId: string, text: string) => string | null
  /** This person's operator replies in this run, oldest first. */
  operatorReplies: OperatorReply[]
  /** Each hold's latest state as the run reported it, by hold id. */
  operatorHoldStates: Record<string, string>
}

/** How a question the host ended shows on its card: data.questionClosed (agion/Agion#2767). */
export interface QuestionClosedNote {
  /** Why the host closed it; none when this build does not know the reason. */
  reason?: QuestionClosedReason
}

/** Marks the question's card as closed by the host. */
function markQuestionClosed(
  setMessages: Dispatch<SetStateAction<StreamingChatMessage[]>>,
  requestId: string,
  questionClosed: QuestionClosedNote,
): void {
  setMessages((prev) => prev.map((m) => (m.type === 'hitl_request' && m.data.requestId === requestId
    ? { ...m, data: { ...m.data, questionClosed } }
    : m)))
}

export function useStreamingMissionChat(
  options: UseStreamingMissionChatOptions
): UseStreamingMissionChatResult {
  const { missionId, executionId: externalExecutionId, enabled = true, skipSubscription = false, acceptsMessages = false } = options
  const onConnectionRestoredRef = useRef(options.onConnectionRestored)
  useEffect(() => {
    onConnectionRestoredRef.current = options.onConnectionRestored
  }, [options.onConnectionRestored])

  const [messages, setMessages] = useState<StreamingChatMessage[]>([])
  const [nodeStatuses, setNodeStatuses] = useState<Map<string, NodeStatus>>(new Map())
  const [pendingHITLs, setPendingHITLs] = useState<ActiveHITLRequest[]>([])
  const [selectedHITLIndex, setSelectedHITLIndex] = useState(0)
  const selectedHITL = pendingHITLs[selectedHITLIndex] ?? null
  // Backward compat alias
  const activeHITL = selectedHITL
  const [isCompleted, setIsCompleted] = useState(false)
  const [isFailed, setIsFailed] = useState(false)
  const [error, setErrorText] = useState<string | null>(null)
  // Agion#2061: an error frame's code lets the UI translate the banner and
  // fall back to the server text. Code and text move together so a later
  // error can never inherit the previous code.
  const [errorCode, setErrorCode] = useState<string | null>(null)
  // The current code, for handlers that clear only one kind of error.
  const errorCodeRef = useRef<string | null>(null)
  const setError = useCallback((message: string | null, code: string | null = null) => {
    errorCodeRef.current = code
    setErrorText(message)
    setErrorCode(code)
  }, [])
  const [stats, setStats] = useState<ExecutionStats>({
    totalInputTokens: 0,
    totalOutputTokens: 0,
    turnCount: 0,
    toolCallCount: 0,
    durationMs: 0,
  })
  const [voiceRoom, setVoiceRoom] = useState<VoiceRoomInfo | null>(null)
  const [operatorReplies, setOperatorReplies] = useState<OperatorReply[]>([])
  const [operatorHoldStates, setOperatorHoldStates] = useState<Record<string, string>>({})
  const [isConnected, setIsConnected] = useState(false)
  const [isPaused, setIsPaused] = useState(false)
  // Pause, resume and stop show their result before the relay answers. The
  // paused state before the latest control frame is kept until the run ends,
  // so a control_forbidden or control_unavailable answer puts it back
  // (agion/Agion#2346, agion/Agion#3024).
  const isPausedRef = useRef(false)
  const pausedBeforeControlRef = useRef<boolean | null>(null)
  // A stop, pause or resume sent again, or the run's end, makes an earlier
  // refusal of one (CONTROL_REFUSALS) old news: its banner goes. A refusal
  // of the new one shows it again.
  const clearControlRefusal = useCallback(() => {
    if (errorCodeRef.current !== null && CONTROL_REFUSALS.has(errorCodeRef.current)) setError(null)
  }, [setError])
  useEffect(() => {
    isPausedRef.current = isPaused
  }, [isPaused])
  // The open questions, for a history load that must keep them.
  const pendingHITLsRef = useRef<ActiveHITLRequest[]>([])
  useEffect(() => {
    pendingHITLsRef.current = pendingHITLs
  }, [pendingHITLs])

  const unsubscribeRef = useRef<(() => void) | null>(null)
  const startTimeRef = useRef<number | null>(null)
  const seenTurnsRef = useRef(new Set<number>())
  const seenToolCallsRef = useRef(new Set<string>())
  // Question request ids this execution's chat already shows. The relay
  // replays pending questions on each subscribe (agion/Agion#2327).
  const seenQuestionsRef = useRef(new Set<string>())
  // Track this user's executionId for HITL isolation.
  // Set from launch response (preferred) or captured from first HITL request (fallback).
  const executionIdRef = useRef<string | null>(externalExecutionId ?? null)
  // Track pending governance decisions keyed by node_id.
  // Consumed by onAgentTurn to attach governance data to the agent response.
  const pendingGovernanceRef = useRef<Map<string, { action: string; reason?: string; trustTier?: string }>>(new Map())

  const closeQuestion = useCallback((requestId: string) => {
    setPendingHITLs((prev) => {
      const next = prev.filter((h) => h.requestId !== requestId)
      setSelectedHITLIndex((idx) => Math.min(idx, Math.max(0, next.length - 1)))
      return next
    })
  }, [])
  const reopenQuestion = useCallback((question: ActiveHITLRequest) => {
    setPendingHITLs((prev) => {
      const at = prev.findIndex((h) => h.requestId === question.requestId)
      const next = at === -1 ? [...prev, question] : prev
      setSelectedHITLIndex(at === -1 ? next.length - 1 : at)
      return next
    })
  }, [])
  // Each answer's delivery state, and a failed or refused one handed back (phase 4, agion/Agion#2327).
  const { sendAnswer, receipt, refusal, questionResult, questionClosed, reset: resetDelivery, returnedAnswer, returnSend } =
    useAnswerDelivery({ setMessages, closeQuestion, reopenQuestion })
  // Each message sent while the agent works, from sending to delivered (phase 6).
  const { sendUserMessage, userMessageReceipt, runEnded, restoreQueued, keptRows, reset: resetUserMessages } =
    useUserMessageDelivery({ setMessages, returnSend })

  // Update ref when external executionId changes (e.g., after launch response)
  useEffect(() => {
    if (externalExecutionId) {
      executionIdRef.current = externalExecutionId
    }
  }, [externalExecutionId])

  // Build subscription options with all event handlers
  const subscriptionOptions = useMemo<MissionSubscriptionOptions>(() => ({
    onNodeStatusUpdate: (update: NodeStatusUpdateMessage) => {
      if (!startTimeRef.current && update.status === 'running') {
        startTimeRef.current = Date.now()
      }

      setNodeStatuses((prev) => {
        const next = new Map(prev)
        next.set(update.node_id, {
          nodeId: update.node_id,
          status: update.status,
          agentId: update.agent_id,
          startedAt: update.started_at,
          completedAt: update.completed_at,
          error: update.error,
        })
        return next
      })

      setMessages((prev) => [...prev, {
        id: `node-${update.node_id}-${Date.now()}`,
        type: 'node_status',
        timestamp: Date.now(),
        missionId: update.mission_id,
        nodeId: update.node_id,
        agentId: update.agent_id,
        data: {
          status: update.status,
          error: update.error,
          startedAt: update.started_at,
          completedAt: update.completed_at,
          nodeLabel: update.node_label,
          outcome: update.outcome,
          blockedReason: update.blocked_reason,
          forcedReason: knownForcedReason(update.forced_reason),
        },
      }])
    },

    onAgentTurn: (turn: AgentTurnMessage) => {
      const id = `turn-${turn.turn_number}`

      // Check for pending governance decision for this node
      const pendingGov = pendingGovernanceRef.current.get(turn.node_id)
      if (pendingGov) {
        pendingGovernanceRef.current.delete(turn.node_id)
      }

      setMessages((prev) => {
        if (prev.some((m) => m.id === id)) return prev
        return [...prev, {
          id,
          type: 'agent_turn',
          timestamp: Date.parse(turn.timestamp),
          missionId: turn.mission_id,
          nodeId: turn.node_id,
          agentId: turn.agent_id,
          data: {
            turnNumber: turn.turn_number,
            userPrompt: turn.user_prompt,
            assistantResponse: turn.assistant_response,
            inputTokens: turn.input_tokens,
            outputTokens: turn.output_tokens,
            maxOutputTokens: turn.max_output_tokens,
            durationMs: turn.duration_ms,
            model: turn.model,
            agentLabel: turn.agent_label,
            ...(pendingGov ? { governance: pendingGov } : {}),
          },
        }]
      })

      if (!seenTurnsRef.current.has(turn.turn_number)) {
        seenTurnsRef.current.add(turn.turn_number)
        setStats((prev) => ({
          ...prev,
          turnCount: prev.turnCount + 1,
          totalInputTokens: prev.totalInputTokens + turn.input_tokens,
          totalOutputTokens: prev.totalOutputTokens + turn.output_tokens,
          durationMs: startTimeRef.current ? Date.now() - startTimeRef.current : 0,
        }))
      }
    },

    // A tool call or result delivered twice shows once, and the tool call count
    // counts each different call once (utils/live-tool-messages.ts,
    // agion/Agion#2003). Their kinds come from the placement module, which maps
    // every generated message type.
    onToolCall: (call: ToolCallMessage) => {
      const message: StreamingChatMessage = {
        id: `tool-call-${call.tool_call_id}`,
        type: streamMessageKind(MessageTypeToolCall),
        timestamp: Date.parse(call.timestamp),
        missionId: call.mission_id,
        nodeId: call.node_id,
        agentId: call.agent_id,
        data: {
          toolCallId: call.tool_call_id,
          toolName: call.tool_name,
          toolInput: call.tool_input,
          agentLabel: call.agent_label,
        },
      }
      setMessages((prev) => withLiveToolMessage(prev, message, toolCallContent))

      const seen = `${call.tool_call_id}\u0000${toolCallContent(message.data)}`
      if (call.tool_call_id && seenToolCallsRef.current.has(seen)) return
      if (call.tool_call_id) seenToolCallsRef.current.add(seen)
      setStats((prev) => ({
        ...prev,
        toolCallCount: prev.toolCallCount + 1,
      }))
    },

    onToolResult: (result: ToolResultMessage) => {
      // The files the result carries, or those its output lists when the host
      // sent them only there, read as the Mission Designer reads them
      // (agion/Agion#2003).
      const outputFiles = toolResultViewFiles(result)
      const message: StreamingChatMessage = {
        id: `tool-result-${result.tool_call_id}`,
        type: streamMessageKind(MessageTypeToolResult),
        timestamp: Date.parse(result.timestamp),
        missionId: result.mission_id,
        nodeId: result.node_id,
        agentId: result.agent_id,
        data: {
          toolCallId: result.tool_call_id,
          toolName: result.tool_name,
          success: result.success,
          output: result.output,
          error: result.error,
          durationMs: result.duration_ms,
          agentLabel: result.agent_label,
          outputFiles: outputFiles.length > 0 ? outputFiles : undefined,
        },
      }
      setMessages((prev) => withLiveToolMessage(prev, message, toolResultContent))
    },

    onHITLRequest: (request: HITLRequestMessage) => {
      // A replayed question the chat already holds adds no second row or
      // open question.
      if (seenQuestionsRef.current.has(request.request_id)) return
      seenQuestionsRef.current.add(request.request_id)
      const isMessage = request.modality === 'message'
      // A question card frame only names its escalation (agion/Agion#3446):
      // the row draws the card from governance and is never an open question,
      // whatever modality the frame carries, so nothing answers it as one.
      const questionCard = questionCardOf(request.governance_context)
      if (questionCard) {
        // A history read before the replay may already hold the card.
        setMessages((prev) => prev.some((m) => isCardRow(m, request.request_id)) ? prev : [...prev, {
          id: `hitl-req-${request.request_id}`,
          type: 'hitl_request',
          timestamp: Date.parse(request.timestamp),
          missionId: request.mission_id,
          nodeId: request.node_id,
          agentId: request.agent_id,
          data: {
            requestId: request.request_id,
            modality: request.modality,
            prompt: request.prompt,
            agentLabel: request.agent_label,
            questionCard,
          },
        }])
        return
      }

      const question = choiceQuestion(request.question)
      const expiresAt = freeTextExpiry(request.question)
      setMessages((prev) => withLiveQuestion(prev, {
        id: `hitl-req-${request.request_id}`,
        type: 'hitl_request',
        timestamp: Date.parse(request.timestamp),
        missionId: request.mission_id,
        nodeId: request.node_id,
        agentId: request.agent_id,
        data: {
          requestId: request.request_id,
          modality: request.modality,
          prompt: request.prompt,
          options: request.options,
          ...(questionOptionDetails(request.option_details) ? { optionDetails: questionOptionDetails(request.option_details) } : {}),
          ...(question ? { question } : {}),
          timeoutMs: request.timeout_ms,
          agentLabel: request.agent_label,
        },
      }))

      // Only track HITL for non-message modalities (messages don't need response)
      if (!isMessage) {
        const govCtx = request.governance_context
        const newHITL: ActiveHITLRequest = {
          requestId: request.request_id,
          executionId: request.execution_id,
          nodeId: request.node_id,
          agentId: request.agent_id,
          prompt: request.prompt,
          options: request.options,
          ...(questionOptionDetails(request.option_details) ? { optionDetails: questionOptionDetails(request.option_details) } : {}),
          ...(question ? { question } : {}),
          ...(expiresAt ? { expiresAt } : {}),
          modality: request.modality,
          timeoutMs: request.timeout_ms,
          receivedAt: Date.now(),
          kind: request.kind,
          governanceContext: govCtx ? {
            ruleName: govCtx.rule_name,
            severity: govCtx.severity,
            action: govCtx.action,
            escalationId: govCtx.escalation_id || undefined,
            inlineDecision: govCtx.inline_decision === true,
          } : undefined,
        }
        setPendingHITLs((prev) => [...prev, newHITL])

        // Bridge to global execution store for sidebar
        const mName = useMissionStore.getState().missions.find(m => m.id === request.mission_id)?.name || ''
        useExecutionStore.getState().addHITLRequest({
          requestId: request.request_id,
          missionId: request.mission_id,
          missionName: mName,
          executionId: request.execution_id,
          nodeId: request.node_id,
          agentId: request.agent_id,
          prompt: request.prompt,
          options: request.options,
          ...(question ? { question } : {}),
          ...(expiresAt ? { expiresAt } : {}),
          modality: request.modality,
          timeoutMs: request.timeout_ms,
          receivedAt: Date.now(),
        })
      }
    },

    onHITLResponse: (response: HITLResponseMessage) => {
      // The relay echoes an answer only after passing it on: the receipt of
      // one this chat sent turns it delivered.
      const ownMessageId = receipt(response.request_id, response.response, response.answer_attempt_id)
      // This chat's answer shows its files as the echo names them: after an
      // accepted answer, the host's copy (agion/Agion#2767).
      const echoedFiles = ownMessageId && response.attachments?.length ? { attachments: rowFiles(response.attachments) } : {}
      setMessages((prev) => {
        // Deduplicate: this chat's own answer, or a message already showing
        // this answer, gets the server data (adds userId/userName) instead of
        // a second bubble. Another answer to the same question is appended.
        const localIdx = ownMessageId
          ? prev.findIndex((m) => m.id === ownMessageId)
          : prev.findIndex((m) => m.type === 'hitl_response'
            && m.data.requestId === response.request_id && m.data.response === response.response)
        if (localIdx !== -1) {
          const updated = [...prev]
          const existing = updated[localIdx]!
          updated[localIdx] = {
            ...existing,
            data: {
              ...existing.data,
              ...echoedFiles,
              userId: response.user_id,
              userName: response.user_name,
            },
          }
          return updated
        }
        // No local message — append (message from another user)
        return [...prev, {
          id: `hitl-resp-${response.request_id}`,
          type: 'hitl_response',
          timestamp: Date.parse(response.timestamp),
          missionId: response.mission_id,
          nodeId: response.node_id,
          data: {
            requestId: response.request_id,
            response: response.response,
            responseTimeMs: response.response_time_ms,
            userId: response.user_id,
            userName: response.user_name,
          },
        }]
      })

      // Remove from pending HITLs array
      setPendingHITLs((prev) => {
        const next = prev.filter((h) => h.requestId !== response.request_id)
        // Clamp selectedIndex if it would be out of bounds
        setSelectedHITLIndex((idx) => Math.min(idx, Math.max(0, next.length - 1)))
        return next
      })

      // Bridge to global execution store
      useExecutionStore.getState().removeHITLRequest(response.request_id)
    },

    onOrchestratorDecision: (decision: OrchestratorDecisionMessage) => {
      setMessages((prev) => [...prev, {
        id: `orch-decision-${decision.iteration}-${Date.now()}`,
        type: 'orchestrator_decision',
        timestamp: Date.parse(decision.timestamp),
        missionId: decision.mission_id,
        data: {
          iteration: decision.iteration,
          reasoning: decision.reasoning,
          action: decision.action,
          targetAgent: decision.target_agent,
          model: decision.model,
          tokensUsed: decision.tokens_used,
          agentLabel: decision.agent_label,
        },
      }])
    },

    onDelegation: (delegation: DelegationMessage) => {
      setMessages((prev) => [...prev, {
        id: `delegation-${delegation.delegation_id}`,
        type: streamMessageKind(MessageTypeDelegation),
        timestamp: Date.parse(delegation.timestamp),
        missionId: delegation.mission_id,
        data: {
          delegationId: delegation.delegation_id,
          agentNodeId: delegation.agent_node_id,
          agentLabel: delegation.agent_label,
          task: delegation.task,
          context: delegation.context,
          depth: delegation.depth,
        },
      }])
    },

    onDelegationResult: (result: DelegationResultMessage) => {
      setMessages((prev) => [...prev, {
        id: `delegation-result-${result.delegation_id}`,
        type: 'delegation_result',
        timestamp: Date.parse(result.timestamp),
        missionId: result.mission_id,
        data: {
          delegationId: result.delegation_id,
          agentNodeId: result.agent_node_id,
          agentLabel: result.agent_label,
          success: result.success,
          summary: result.summary,
          error: result.error,
          durationMs: result.duration_ms,
          outcome: result.outcome,
          blockedReason: result.blocked_reason,
        },
      }])
    },

    onAgentThinking: (thinking: AgentThinkingMessage) => {
      const thinkingId = `thinking-${thinking.node_id}`
      setMessages((prev) => {
        const existing = prev.findIndex((m) => m.id === thinkingId)
        const msg: StreamingChatMessage = {
          id: thinkingId,
          type: 'agent_thinking',
          timestamp: Date.parse(thinking.timestamp),
          missionId: thinking.mission_id,
          nodeId: thinking.node_id,
          agentId: thinking.agent_id,
          data: {
            turnNumber: thinking.turn_number,
            content: thinking.content,
            redacted: thinking.redacted,
            model: thinking.model,
            agentLabel: thinking.agent_label,
          },
        }
        if (existing >= 0) {
          // Replace interim thinking with latest
          const next = [...prev]
          next[existing] = msg
          return next
        }
        return [...prev, msg]
      })
    },

    onGovernanceDecision: (decision: GovernanceDecisionMessage) => {
      if (decision.action === 'deny' || decision.action === 'escalate') {
        const alertId = `gov-${decision.node_id}-${Date.now()}`
        // Show as governance_event SystemMessage
        setMessages((prev) => [...prev, {
          id: alertId,
          type: streamMessageKind(MessageTypeGovernanceDecision),
          timestamp: Date.now(),
          missionId: decision.mission_id,
          nodeId: decision.node_id,
          agentId: decision.agent_id,
          data: {
            decision: decision.action === 'deny' ? 'denied' : 'escalated',
            action: decision.action,
            reason: decision.reason,
            ruleName: decision.rule_name,
            trustTier: decision.trust_tier,
          },
        }])

        // Bridge to global execution store for sidebar
        const mName = useMissionStore.getState().missions.find(m => m.id === decision.mission_id)?.name || ''
        useExecutionStore.getState().addGovernanceAlert({
          id: alertId,
          missionId: decision.mission_id,
          missionName: mName,
          nodeId: decision.node_id,
          action: decision.action as 'deny' | 'escalate',
          reason: decision.reason,
          ruleName: decision.rule_name,
          trustTier: decision.trust_tier,
          receivedAt: Date.now(),
        })
      } else if (decision.action === 'obfuscate') {
        // Show obfuscate as a visible governance event
        setMessages((prev) => [...prev, {
          id: `gov-obfuscate-${decision.node_id}-${Date.now()}`,
          type: 'governance_event',
          timestamp: Date.now(),
          missionId: decision.mission_id,
          nodeId: decision.node_id,
          agentId: decision.agent_id,
          data: {
            decision: 'obfuscated',
            action: decision.action,
            reason: decision.reason,
            ruleName: decision.rule_name,
            trustTier: decision.trust_tier,
          },
        }])
        // Also stash for agent turn governance badge
        pendingGovernanceRef.current.set(decision.node_id, {
          action: decision.action,
          reason: decision.reason,
          trustTier: decision.trust_tier,
        })
      } else {
        // allow — stash for the next agent turn on this node
        pendingGovernanceRef.current.set(decision.node_id, {
          action: decision.action,
          reason: decision.reason,
          trustTier: decision.trust_tier,
        })
      }
    },

    onTranscriptInterim: (transcript: TranscriptInterimMessage) => {
      // Update or add interim transcript message
      setMessages((prev) => {
        const interimId = `transcript-interim-${transcript.node_id}`
        const existing = prev.findIndex((m) => m.id === interimId)
        const msg: StreamingChatMessage = {
          id: interimId,
          type: 'transcript',
          timestamp: Date.now(),
          missionId: transcript.mission_id,
          nodeId: transcript.node_id,
          data: {
            speaker: transcript.speaker,
            text: transcript.text,
            isFinal: false,
            confidence: transcript.confidence,
            language: transcript.language,
          },
        }
        if (existing >= 0) {
          const next = [...prev]
          next[existing] = msg
          return next
        }
        return [...prev, msg]
      })
    },

    onTranscriptFinal: (transcript: TranscriptFinalMessage) => {
      setMessages((prev) => {
        // Remove interim transcript for this node
        const interimId = `transcript-interim-${transcript.node_id}`
        const filtered = prev.filter((m) => m.id !== interimId)
        return [...filtered, {
          id: `transcript-final-${Date.now()}-${transcript.speaker}`,
          type: 'transcript',
          timestamp: Date.now(),
          missionId: transcript.mission_id,
          nodeId: transcript.node_id,
          data: {
            speaker: transcript.speaker,
            text: transcript.text,
            isFinal: true,
            confidence: transcript.confidence,
            language: transcript.language,
          },
        }]
      })
    },

    onVoiceRoomReady: (room: VoiceRoomReadyMessage) => {
      // Capture executionId early (voice room ready arrives before HITL requests)
      if (room.execution_id && !executionIdRef.current) {
        executionIdRef.current = room.execution_id
      }
      setVoiceRoom({
        serverUrl: room.server_url,
        participantToken: room.participant_token,
        roomName: room.room_name,
        participantIdentity: room.participant_identity,
        executionId: room.execution_id,
      })
    },

    // The host names the sub-mission in mission_id and execution_id; the
    // parent, which this chat runs, in parent_mission_id.
    onMissionTrigger: (trigger: MissionTriggerMessage) => {
      setMessages((prev) => [...prev, {
        id: `trigger-${trigger.mission_id}-${Date.now()}`,
        type: 'mission_trigger',
        timestamp: Date.parse(trigger.timestamp),
        missionId: trigger.parent_mission_id,
        nodeId: trigger.parent_node_id,
        data: {
          parentMissionId: trigger.parent_mission_id,
          parentNodeId: trigger.parent_node_id,
          childMissionId: trigger.mission_id,
          childExecutionId: trigger.execution_id,
          input: trigger.input,
        },
      }])
    },

    // Operator conversation takeover (agion/Agion#3435). A person's reply is
    // its own kind, so it never shows under the agent's name; the hold's
    // notice is the runner's localised text, shown as it came.
    onOperatorTurn: (turn: OperatorTurnMessage) => {
      if (!operatorTakeoverEnabled()) return
      const id = `operator-turn-${turn.message_id}`
      setMessages((prev) => (prev.some((m) => m.id === id) ? prev : [...prev, {
        id,
        type: streamMessageKind(MessageTypeOperatorTurnEvent),
        timestamp: Date.parse(turn.created_at) || Date.now(),
        missionId: turn.mission_id,
        data: {
          holdId: turn.hold_id,
          messageId: turn.message_id,
          operatorUserId: turn.operator_user_id,
          operatorDisplayName: turn.operator_display_name,
          text: turn.text,
        },
      }]))
    },

    // An end-user message the runner delivered during a hold shows to the
    // operator as the customer's message, under the id the sender's own
    // view gives it (useUserMessageDelivery), so the sender sees it once and
    // a repeat adds nothing. Its byline is the sender's: by id, with the name
    // an earlier row of the same person carries.
    onOperatorEndUserTurn: (turn: OperatorEndUserTurnMessage) => {
      if (!operatorTakeoverEnabled()) return
      const id = `user-message-${turn.message_id}`
      setMessages((prev) => {
        if (prev.some((m) => m.id === id)) return prev
        const userName = prev.find((m) => m.type === 'hitl_response' && m.data.userId === turn.sender_user_id && m.data.userName)?.data.userName
        return [...prev, {
          id,
          type: streamMessageKind(MessageTypeOperatorEndUserTurnEvent),
          timestamp: Date.parse(turn.created_at) || Date.now(),
          missionId: turn.mission_id,
          data: {
            requestId: '',
            response: turn.text,
            modality: 'text',
            userId: turn.sender_user_id,
            ...(userName ? { userName } : {}),
          },
        }]
      })
    },

    onOperatorHold: (hold: OperatorHoldMessage) => {
      if (!operatorTakeoverEnabled()) return
      setOperatorHoldStates((prev) => ({ ...prev, [hold.hold_id]: hold.state }))
      const id = `operator-hold-${hold.hold_id}-${hold.state}`
      setMessages((prev) => (prev.some((m) => m.id === id) ? prev : [...prev, {
        id,
        type: streamMessageKind(MessageTypeOperatorHoldEvent),
        timestamp: Date.parse(hold.at) || Date.now(),
        missionId: hold.mission_id,
        data: { message: hold.notice_text, severity: 'info', holdId: hold.hold_id, holdState: hold.state },
      }]))
    },

    // A receipt only changes the reply it is for.
    onOperatorMessageReceipt: (receipt: OperatorMessageReceiptMessage) => {
      setOperatorReplies((prev) => prev.map((reply) => (reply.messageId === receipt.message_id
        ? {
            ...reply,
            status: receipt.status === 'accepted' ? 'accepted' : 'refused',
            ...(receipt.reason ? { reason: receipt.reason } : {}),
          }
        : reply)))
    },

    // A receipt only moves the message it is for (useUserMessageDelivery).
    onUserMessageReceipt: (receipt: UserMessageReceiptMessage) => {
      userMessageReceipt(receipt)
    },

    // The host's answer to the subscribe: this user's messages that still
    // wait show as queued again after a reload (useUserMessageDelivery).
    onUserMessageQueued: (queued: UserMessageQueuedMessage) => {
      restoreQueued(queued)
    },

    // The host's answer to one of this user's answer attempts (agion/Agion#2767).
    // Only mission.question.closed closes a question.
    onQuestionAnswerResult: (result: QuestionAnswerResultMessage) => {
      questionResult(result)
    },

    // The host ended a question: its card says closed or expired, and the
    // composer no longer answers it (agion/Agion#2767).
    onQuestionClosed: (closed: QuestionClosedMessage) => {
      questionClosed(closed.request_id)
      markQuestionClosed(setMessages, closed.request_id, isQuestionClosedReason(closed.reason) ? { reason: closed.reason } : {})
    },

    onCompleted: (completed: MissionCompletedMessage) => {
      runEnded()
      setIsCompleted(true)
      setIsPaused(false)
      pausedBeforeControlRef.current = null
      clearControlRefusal()
      setMessages((prev) => [...prev, {
        id: `mission-completed-${Date.now()}`,
        type: 'mission_completed',
        timestamp: Date.now(),
        missionId: completed.mission_id,
        data: {
          outcome: completed.outcome,
          durationMs: completed.duration_ms,
          outputs: completed.outputs,
        },
      }])

      // Clean up global execution store
      const store = useExecutionStore.getState()
      store.removeHITLRequestsForMission(completed.mission_id)
      store.removeGovernanceAlertsForMission(completed.mission_id)
    },

    onFailed: (failed: MissionFailedMessage) => {
      runEnded()
      setIsFailed(true)
      setIsPaused(false)
      pausedBeforeControlRef.current = null
      setError(failed.error)
      setMessages((prev) => [...prev, {
        id: `mission-failed-${Date.now()}`,
        type: 'mission_failed',
        timestamp: Date.now(),
        missionId: failed.mission_id,
        data: {
          error: failed.error,
          failedNodeId: failed.failed_node_id,
          outcome: failed.outcome,
        },
      }])

      // Clean up global execution store
      const store = useExecutionStore.getState()
      store.removeHITLRequestsForMission(failed.mission_id)
      store.removeGovernanceAlertsForMission(failed.mission_id)
    },

    // The server withdrew the run's provisional host-disconnect end (P-095
    // Amendment S04-5): the chat shows the run running again. The failure's
    // banner goes; a stream error with a code stays.
    onConnectionRestored: (restored: ConnectionRestoredMessage) => {
      setIsFailed(false)
      if (errorCodeRef.current === null) setError(null)
      // The relay held the run's open questions through the provisional end
      // (Amendment S04-7). The chat drops its own copies and takes them from
      // the replay a subscribe again brings; the caller reloads the messages.
      const open = new Set(pendingHITLsRef.current.map((h) => h.requestId))
      setMessages((prev) => prev.filter((m) => !(m.type === 'hitl_request'
        && typeof m.data.requestId === 'string' && open.has(m.data.requestId))))
      for (const requestId of open) seenQuestionsRef.current.delete(requestId)
      setPendingHITLs([])
      setSelectedHITLIndex(0)
      getMissionStatusService().resubscribe(restored.mission_id, restored.execution_id)
      onConnectionRestoredRef.current?.()
    },

    // A later reconnect that the relay accepts ends an "unavailable" banner
    // left after the retries ran out; any other error stays.
    onSubscribed: () => {
      if (errorCodeRef.current === 'subscription_unavailable') setError(null)
    },

    onError: (err) => {
      // A refused answer shows its reason on the answer, which goes back to
      // the composer; the banner is for errors no answer explains.
      if (refusal(err)) return
      // The relay refused a pause, resume or stop: the run did not change.
      if (err.code && CONTROL_REFUSALS.has(err.code) && pausedBeforeControlRef.current !== null) {
        setIsPaused(pausedBeforeControlRef.current)
        pausedBeforeControlRef.current = null
      }
      setError(err.message, err.code ?? null)
    },
  }), [receipt, refusal, questionResult, questionClosed, userMessageReceipt, runEnded, restoreQueued, clearControlRefusal])

  // Subscribe/unsubscribe when missionId + executionId are both available.
  // No subscription without executionId — server requires it for isolation.
  // Skip subscription for completed/failed executions (history-only mode).
  useEffect(() => {
    if (!enabled || !missionId || !externalExecutionId) return

    if (skipSubscription) {
      // F15 (chat-ui catch-up): the local-mock branch was deleted along with
      // the home-grown dev mock layer. skipSubscription now only fires for
      // completed / failed / cancelled executions — no live WS to attach to.
      setError(null)
      setIsConnected(false)
      return () => {
        setIsConnected(false)
      }
    }

    // Reset state for new execution. A message this execution still waits
    // to deliver keeps its row and its tracking, so a subscribe again for
    // the same execution does not lose it.
    setMessages((prev) => keptRows(externalExecutionId, prev))
    setNodeStatuses(new Map())
    setPendingHITLs([])
    setSelectedHITLIndex(0)
    setIsCompleted(false)
    setIsFailed(false)
    pausedBeforeControlRef.current = null
    setError(null)
    setVoiceRoom(null)
    resetDelivery()
    resetUserMessages(externalExecutionId)
    setStats({
      totalInputTokens: 0,
      totalOutputTokens: 0,
      turnCount: 0,
      toolCallCount: 0,
      durationMs: 0,
    })
    startTimeRef.current = null
    seenTurnsRef.current = new Set<number>()
    seenToolCallsRef.current = new Set<string>()
    seenQuestionsRef.current = new Set<string>()
    executionIdRef.current = externalExecutionId

    const service = getMissionStatusService()
    // A subscribe that finishes after this effect's cleanup is dropped at
    // once: a handler left behind would keep the execution's subscription
    // open, and a later subscribe for it would send no frame. Each run has
    // its own handler: the client keeps handlers by identity, so a run that
    // shared the memoized options with the next one (a React StrictMode
    // remount) would remove the next one's handler with its own.
    let cancelled = false
    const handler: MissionSubscriptionOptions = { ...subscriptionOptions }
    service.subscribeAsync(missionId, externalExecutionId, handler).then((unsub) => {
      if (cancelled) {
        unsub()
        return
      }
      unsubscribeRef.current = unsub
      setIsConnected(true)
      console.log('[useStreamingMissionChat] Subscribed to mission', missionId, 'execution', externalExecutionId)

      // Signal readiness for subscriber-gated dispatch
      service.sendMissionReady({ missionId, executionId: externalExecutionId })
    }).catch((err) => {
      if (cancelled) return
      console.error('[useStreamingMissionChat] Failed to subscribe:', err)
      setError(`Connection failed: ${err.message}`)
      setIsConnected(false)
    })

    // Listen for connection state changes. A transient 'error' sets the
    // banner; once the state reaches 'connected' again — a reconnect that
    // resubscribed — the connection is no longer lost, so the banner clears.
    // An error unrelated to the socket (a refused answer, a forbidden
    // control) goes through onError/sendAnswer, not this listener, so it is
    // untouched here.
    const unsubState = service.onConnectionStateChange((state) => {
      setIsConnected(state === 'connected')
      if (state === 'error') {
        setError('WebSocket connection lost')
      } else if (state === 'connected') {
        setError(null)
      }
    })

    return () => {
      cancelled = true
      unsubState()
      if (unsubscribeRef.current) {
        unsubscribeRef.current()
        unsubscribeRef.current = null
      }
      setIsConnected(false)
    }
  }, [enabled, missionId, externalExecutionId, skipSubscription, subscriptionOptions, resetDelivery, resetUserMessages, keptRows])

  // Respond to HITL request
  const respondToHITL = useCallback((requestId: string, response: string, attachments?: HITLAttachment[], decision?: HITLDecision, answer?: QuestionReply) => {
    // Only an open question takes an answer: the answer carries that question's
    // node and comes back to it if it fails. An answer to a closed question
    // went out under another question's node and the relay refused it.
    const target = pendingHITLs.find((h) => h.requestId === requestId)
    if (!missionId || !target) return

    // The answer shows its delivery state; the question closes while it is on its way.
    sendAnswer(missionId, target, response, attachments ?? [], 'choice', decision, answer)
  }, [missionId, pendingHITLs, sendAnswer])

  // Send a user message to the mission chat. A selected free-text question
  // takes it as its answer (phase 4, agion/Agion#2327). With no question open,
  // a live run that accepts messages takes it as a message the agent reads at
  // its next turn (phase 6); a question with choices waits for its choice.
  // Otherwise nothing is sent or shown, and the result is false so the
  // composer keeps the text and files.
  const sendMessage = useCallback((message: string, attachments?: HITLAttachment[]): boolean => {
    if (!missionId) return false
    const trimmed = message.trim()
    if (!trimmed && (!attachments || attachments.length === 0)) return false

    const freeTextHITL = selectedHITL && !selectedHITL.options?.length ? selectedHITL : null
    if (freeTextHITL) {
      // The answer shows its delivery state, and the question and the sidebar
      // entry close while it is on its way; a failed answer brings them back.
      sendAnswer(missionId, freeTextHITL, trimmed, attachments ?? [], 'composer')
      return true
    }
    const execId = executionIdRef.current
    if (selectedHITL || !acceptsMessages || !execId || isCompleted || isFailed) return false
    sendUserMessage(missionId, execId, trimmed, attachments ?? [])
    return true
  }, [missionId, selectedHITL, sendAnswer, acceptsMessages, isCompleted, isFailed, sendUserMessage])

  // An operator's reply (agion/Agion#3435): the relay sets the sender from the
  // connection, and the runner takes it only from the hold's operator.
  const sendOperatorMessage = useCallback((holdId: string, text: string): string | null => {
    const execId = executionIdRef.current
    if (!operatorTakeoverEnabled() || !missionId || !execId) return null
    const messageId = crypto.randomUUID()
    const sent = getMissionStatusService().sendOperatorMessage({
      message_id: messageId,
      mission_id: missionId,
      execution_id: execId,
      hold_id: holdId,
      text,
    })
    setOperatorReplies((prev) => [...prev, { messageId, holdId, text, status: sent === false ? 'not_sent' : 'sending' }])
    return messageId
  }, [missionId])

  // Navigate to a specific pending HITL by index
  const selectHITL = useCallback((index: number) => {
    setSelectedHITLIndex(Math.max(0, Math.min(index, pendingHITLs.length - 1)))
  }, [pendingHITLs.length])

  // Expire HITL requests that have timed out
  useEffect(() => {
    if (pendingHITLs.length === 0) return

    const timer = setInterval(() => {
      const now = Date.now()
      setPendingHITLs((prev) => {
        const next = prev.filter((h) => {
          const { from, lastsMs } = questionCountdown(h)
          if (!lastsMs) return true
          // Grace period: keep for 3 extra seconds after expiry for UX
          return from + lastsMs + 3000 > now
        })
        if (next.length !== prev.length) {
          setSelectedHITLIndex((idx) => Math.min(idx, Math.max(0, next.length - 1)))
        }
        return next.length !== prev.length ? next : prev
      })
    }, 1000)

    return () => clearInterval(timer)
  }, [pendingHITLs.length])

  // Seed historical messages (replaces current messages for execution
  // switching). A message this execution still waits to deliver has no
  // history row yet, so its row stays after the history, whether the host's
  // list of waiting messages arrived before the history or after it.
  const setInitialMessages = useCallback((msgs: StreamingChatMessage[]) => {
    setMessages((prev) => {
      const seeded = new Set(msgs.map((m) => m.id))
      const waiting = keptRows(executionIdRef.current, prev).filter((m) => !seeded.has(m.id))
      // A question of this execution still open arrived before its history
      // (the relay replays it on subscribe); it keeps its choices over the
      // history's text of it. A question card the relay replayed is never an
      // open question and the Edge history keeps no row for it, so it stays
      // too, unless the history holds the card itself (agion/Agion#3446).
      const open = new Set(pendingHITLsRef.current
        .filter((h) => h.executionId === executionIdRef.current)
        .map((h) => h.requestId))
      const questions = prev.filter((m) => {
        if (m.type !== 'hitl_request' || seeded.has(m.id)) return false
        const requestId = m.data.requestId
        if (typeof requestId !== 'string') return false
        if (open.has(requestId)) return true
        return !!m.data.questionCard && !msgs.some((h) => isCardRow(h, requestId))
      })
      return questions.reduce(withLiveQuestion, waiting.length ? [...msgs, ...waiting] : msgs)
    })
  }, [keptRows])

  // Notify voice user joined
  const notifyVoiceUserJoined = useCallback(() => {
    if (!missionId || !voiceRoom) return

    const service = getMissionStatusService()
    service.sendVoiceUserJoined({
      missionId,
      executionId: voiceRoom.executionId,
    })
  }, [missionId, voiceRoom])

  // F5 (chat-ui catch-up): request mission cancellation via streaming WS.
  // No-op when the mission has no execution yet or the WS is offline.
  const stopMission = useCallback(() => {
    const execId = executionIdRef.current
    if (!missionId || !execId) return
    const service = getMissionStatusService()
    service.sendMissionStop({ missionId, executionId: execId })
    pausedBeforeControlRef.current = isPausedRef.current
    setIsPaused(false)
    clearControlRefusal()
  }, [missionId, clearControlRefusal])

  // Pause/resume mirror the designer's controls (admin-ui
  // useMissionWorkflowExecution): the frame is fire-and-forget and the host
  // pauses at its next checkpoint, so paused state is tracked locally.
  const pauseMission = useCallback(() => {
    const execId = executionIdRef.current
    if (!missionId || !execId) return
    const service = getMissionStatusService()
    service.sendMissionPause({ missionId, executionId: execId })
    pausedBeforeControlRef.current = isPausedRef.current
    setIsPaused(true)
    clearControlRefusal()
  }, [missionId, clearControlRefusal])

  const resumeMission = useCallback(() => {
    const execId = executionIdRef.current
    if (!missionId || !execId) return
    const service = getMissionStatusService()
    service.sendMissionResume({ missionId, executionId: execId })
    pausedBeforeControlRef.current = isPausedRef.current
    setIsPaused(false)
    clearControlRefusal()
  }, [missionId, clearControlRefusal])

  return {
    messages,
    nodeStatuses,
    activeHITL,
    pendingHITLs,
    selectedHITLIndex,
    selectedHITL,
    selectHITL,
    isConnected,
    isCompleted,
    isFailed,
    error,
    errorCode,
    stats,
    voiceRoom,
    sendMessage,
    respondToHITL,
    returnedAnswer,
    notifyVoiceUserJoined,
    stopMission,
    isPaused,
    pauseMission,
    resumeMission,
    setInitialMessages,
    sendOperatorMessage,
    operatorReplies,
    operatorHoldStates,
  }
}
